import type { PickerItemData } from "@design-system/Picker/Picker.types";
import {
    makeItem,
    type PickerPlaygroundFeatures,
} from "./PickerPlayground.lib";

/**
 * A fake server-paged directory for the paged stage and usage examples:
 * 200 generated company names served 20 at a time after a simulated delay.
 */
export const COMPANY_PAGE_SIZE = 20;
export const COMPANY_LATENCY_MS = 600;

const namePrefixes = [
    "Acme",
    "Bluebird",
    "Canyon",
    "Delta",
    "Evergreen",
    "Falcon",
    "Granite",
    "Harbor",
    "Ironwood",
    "Juniper",
    "Keystone",
    "Lakeside",
    "Maple",
    "Northwind",
    "Orchard",
    "Pioneer",
    "Quartz",
    "Redwood",
    "Summit",
    "Tidewater",
];

const nameSuffixes = [
    "Foods",
    "Group",
    "Holdings",
    "Labs",
    "Logistics",
    "Partners",
    "Studio",
    "Supply",
    "Traders",
    "Works",
];

/** Every company, alphabetical, decorated per the sandbox toggles. */
export const buildCompanyItems = ({
    hasDescriptions,
    hasRightText,
}: Pick<PickerPlaygroundFeatures, "hasDescriptions" | "hasRightText">): Array<
    PickerItemData<string>
> =>
    namePrefixes.flatMap((prefix, prefixIndex) =>
        nameSuffixes.map((suffix, suffixIndex) => {
            const index = prefixIndex * nameSuffixes.length + suffixIndex;
            return makeItem({
                label: `${prefix} ${suffix}`,
                description: hasDescriptions
                    ? `Vendor since ${2010 + (index % 15)}`
                    : undefined,
                rightText: hasRightText
                    ? `${((index * 37) % 400) + 1} transactions`
                    : undefined,
            });
        }),
    );

/** The rows a server search returns: a case-insensitive label match. */
export const searchCompanies = (
    items: ReadonlyArray<PickerItemData<string>>,
    query: string,
): ReadonlyArray<PickerItemData<string>> => {
    const needle = query.trim().toLowerCase();
    return needle === ""
        ? items
        : items.filter((item) => item.label.toLowerCase().includes(needle));
};

/** One page of the fake API, resolved after the simulated delay. */
export const fetchCompanyPage = ({
    items,
    search,
    offset,
}: {
    items: ReadonlyArray<PickerItemData<string>>;
    search: string;
    offset: number;
}): Promise<Array<PickerItemData<string>>> =>
    new Promise((resolve) => {
        window.setTimeout(
            () =>
                resolve(
                    searchCompanies(items, search).slice(
                        offset,
                        offset + COMPANY_PAGE_SIZE,
                    ),
                ),
            COMPANY_LATENCY_MS,
        );
    });
